import { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { TextPlugin } from "gsap/TextPlugin";

import "./OurFeatures.scss";
import LayeredImage from "../../components/shared/LayeredImage";

import ImgJaggedLine from "../../assets/images/jagged-line.svg";
import ImgMixerThunder from "../../assets/images/mixer-thunder.svg";
import ImgMixerPipe from "../../assets/images/mixer-pipe.png";
import ImgMixerCoin from "../../assets/images/mixer-coin.png";
import ImgLightMixer from "../../assets/images/light-mixer.svg";
import ImgPromptOn from "../../assets/images/prompt-on.png";
import ImgPromptOff from "../../assets/images/prompt-off.png";
import ImgPromptCloud1 from "../../assets/images/prompt-cloud-1.svg";
import ImgPromptCloud2 from "../../assets/images/prompt-cloud-2.svg";
import ImgLightPromptify from "../../assets/images/light-promptify.svg";

import ImgMagicIcon from "../../assets/images/magic-icon.png";
import ImgChatIcon from "../../assets/images/chat-icon.png";
import BadgeChip from "../../components/Home/BadgeChip";

export default function OurFeatures({ position }) {
	const component = useRef();
	const nativeTitleRef = useRef();
	const nativeDescriptionRef = useRef();
	const nativeHasAnimated = useRef(false);

	const promptifyTitleRef = useRef();
	const promptifyDescriptionRef = useRef();
	const promptifyHasAnimated = useRef(false);
	const promptLayeredImgRef = useRef();
	const promptImgRef = useRef();
	const promptImgHasAnimated = useRef(false);

	useLayoutEffect(() => {
		// Animate native title when visible
		if (nativeTitleRef.current) {
			// calculate the position of the native title
			const nativeTitleRectBottom =
				nativeTitleRef.current.offsetTop +
				nativeTitleRef.current.offsetHeight;

			// start the animation when the our-features section becomes visible
			if (
				position >= nativeTitleRectBottom - window.innerHeight * 1.0 && // 1.0 means as soon as the title is in view
				!nativeHasAnimated.current
			) {
				const tl = gsap.timeline();
				tl.to(nativeTitleRef.current.querySelector(".bold-txt"), {
					duration: 0.3,
					text: "Native",
					ease: "none",
				})
					.to(nativeTitleRef.current.querySelector(".normal-txt"), {
						duration: 0.3,
						text: "Mixer",
						ease: "none",
					})
					.to(nativeDescriptionRef.current, {
						duration: 5,
						text: "StormBot’s native mixer ensures full on-chain anonymity for every transaction, right from Telegram. Whether you're buying, sending, or withdrawing, funds are routed through stealth addresses, hashed commitments, and ephemeral wallets using a trustless smart contract and private relayers. With randomized delays and encrypted confirmations, every action is untraceable, unlinkable, and gasless. No dashboards, no signatures, just invisible execution by default.",
						ease: "none",
					});

				nativeHasAnimated.current = true;
			}
		}

		// Animate promptify title when visible
		if (promptifyTitleRef.current) {
			// calculate the position of the promptify title
			const promptifyTitleRectBottom =
				promptifyTitleRef.current.offsetTop +
				promptifyTitleRef.current.offsetHeight;

			// start the animation when the our-features section becomes visible
			if (
				position >=
					promptifyTitleRectBottom - window.innerHeight * 1.0 && // 1.0 means as soon as the title is in view
				!promptifyHasAnimated.current
			) {
				// Animate the bold "Promptify" word
				const tl = gsap.timeline();
				tl.to(promptifyTitleRef.current.querySelector(".bold-txt"), {
					duration: 0.4,
					text: "Promptify:",
					ease: "none",
				})
					.to(
						promptifyTitleRef.current.querySelector(".normal-txt"),
						{
							duration: 1.5,
							text: "Natural Language, Private Execution",
							ease: "none",
						}
					)
					.to(promptifyDescriptionRef.current, {
						duration: 3.2,
						text: "Promptify Mode is your gateway to lightning-fast, AI-powered transactions. With a single command, you can execute complex trades, swaps, and more, all while staying fully anonymous. Just type your request, and let StormBot handle the rest, all without revealing your identity or transaction details.",
						ease: "none",
					});

				promptifyHasAnimated.current = true;
			}

			// Animate the flickering effect on the promptify image
			if (promptLayeredImgRef.current) {
				const promptLayeredImgBottom =
					promptLayeredImgRef.current.offsetTop +
					promptLayeredImgRef.current.offsetHeight;

				// start the flickering effect when the promptify image is in view
				if (
					position >=
						promptLayeredImgBottom - window.innerHeight * 1 &&
					!promptImgHasAnimated.current
				) {
					const promptImg = promptImgRef.current;
					const tl = gsap.timeline();

					// Flickering effect on the prompt image
					tl.set(promptImg, { attr: { src: ImgPromptOn } }, 0) // turn on
						.set(promptImg, { attr: { src: ImgPromptOff } }, 0.1) // turn off
						.set(promptImg, { attr: { src: ImgPromptOn } }, 0.3) // on
						.set(promptImg, { attr: { src: ImgPromptOff } }, 0.38) // off
						.set(promptImg, { attr: { src: ImgPromptOn } }, 0.53) // on
						.set(promptImg, { attr: { src: ImgPromptOff } }, 0.63) // off
						.set(promptImg, { attr: { src: ImgPromptOn } }, 0.73); // finally on

					promptImgHasAnimated.current = true;
				}
			}
		}
	}, [position]);

	return (
		<>
			{/* Add the ref to the main container */}
			<div className="our-features" id="our-features" ref={component}>
				<div className="our-features-content-wrapper content-wrapper-lg">
					<div className="our-features-content">
						<div className="title-wrapper">
							<h1 className="our-features-title">Our Features</h1>
							<img
								className="jagged-line"
								src={ImgJaggedLine}
								alt="Jagged Line"
							/>
						</div>

						<div className="our-features-items">
							<div className="our-features-item native-mixer">
								<div className="item-content">
									{/* Add the ref to the target h2 element */}
									<h2
										className="item-title"
										ref={nativeTitleRef}
									>
										<span className="bold-txt"></span>{" "}
										<span className="normal-txt"></span>
									</h2>
									<div className="item-description-wrapper">
										<p className="item-description-hidden">
											StormBot’s native mixer ensures full
											on-chain anonymity for every
											transaction, right from Telegram.
											Whether you're buying, sending, or
											withdrawing, funds are routed
											through stealth addresses, hashed
											commitments, and ephemeral wallets
											using a trustless smart contract and
											private relayers. With randomized
											delays and encrypted confirmations,
											every action is untraceable,
											unlinkable, and gasless. No
											dashboards, no signatures, just
											invisible execution by default.
										</p>
										<p
											className="item-description"
											ref={nativeDescriptionRef}
										></p>
									</div>
								</div>

								<LayeredImage
									className="item-img"
									width={600}
									height={400}
								>
									<img
										className="light-mixer"
										src={ImgLightMixer}
										alt="Light Mixer"
									/>
									<img
										className="thunder"
										src={ImgMixerThunder}
										alt="Thunder"
									/>
									<img
										className="pipe shake-slow-2"
										src={ImgMixerPipe}
										alt="Pipe"
									/>
									<img
										className="coin shake-slow-3"
										src={ImgMixerCoin}
										alt="Coin"
									/>

									<BadgeChip className="native-mixer-chip">
										<img
											className="badge-chip-icon"
											src={ImgMagicIcon}
											alt="Magic Icon"
										/>
										Native Mixer
									</BadgeChip>
								</LayeredImage>
							</div>

							<div className="our-features-item promptify-mode">
								<div className="item-content">
									<h2
										className="item-title"
										ref={promptifyTitleRef}
									>
										<span className="bold-txt"></span>{" "}
										<span className="normal-txt"></span>
									</h2>
									<div className="item-description-wrapper">
										<p className="item-description-hidden">
											Promptify Mode is your gateway to
											lightning-fast, AI-powered
											transactions. With a single command,
											you can execute complex trades,
											swaps, and more, all while staying
											fully anonymous. Just type your
											request, and let StormBot handle the
											rest, all without revealing your
											identity or transaction details.
										</p>
										<p
											className="item-description"
											ref={promptifyDescriptionRef}
										></p>
									</div>
								</div>

								<LayeredImage
									className="item-img"
									width={550}
									height={473}
									ref={promptLayeredImgRef}
								>
									<img
										className="light-promptify"
										src={ImgLightPromptify}
										alt="Light Promptify"
									/>
									<img
										className="cloud-1"
										src={ImgPromptCloud1}
										alt="Cloud"
									/>
									<img
										className="cloud-2"
										src={ImgPromptCloud2}
										alt="Cloud"
									/>

									<div className="prompt">
										<img
											className="shake-slow-4"
											src={ImgPromptOff}
											alt="Promptify Mode"
											ref={promptImgRef}
										/>
									</div>

									<BadgeChip className="promptify-chip">
										<img
											className="badge-chip-icon"
											src={ImgChatIcon}
											alt="Chat Icon"
										/>
										Promptify
									</BadgeChip>
								</LayeredImage>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
